import { tr } from "@faker-js/faker";

// Reusable components
const AddressSection = ({ recipientDetails, hideMobileNumber }) => (
  <div className="w-[60%]">
    <p className="font-bold">To:</p>
    <p className="font-bold">{recipientDetails.name}</p>
    <p>{recipientDetails.address}</p>
    <p>
      {recipientDetails.city}, {recipientDetails.state} - {recipientDetails.zip}
    </p>
    <p>Landmark: {recipientDetails.landmark}</p>
    {!hideMobileNumber && (
      <p>
        Mobile:{" "}
        <span className="font-bold">{recipientDetails.mobile.join(", ")}</span>
      </p>
    )}
  </div>
);

const LogoSection = ({ includeLogo, logoUrl }) => (
  <div className="w-[40%] flex justify-end p-2">
    {includeLogo && (
      <div className="w-full h-full border-2 border-dashed border-secondary/30 rounded-md flex items-center justify-center p-2 text-center">
        <span className="text-[10px] font-bold text-secondary/60 leading-tight font-nunito">
          Your Logo Will Appear Here
        </span>
      </div>
    )}
  </div>
);

const OrderInfoSection = ({ orderDetails }) => (
  <div className="flex flex-col justify-center">
    <p>
      <b>Invoice No:</b> {orderDetails.invoiceNo}
    </p>
    <p>
      <b>Shipping ID:</b> {orderDetails.shippingId}
    </p>
    <p>
      <b>Order ID:</b> {orderDetails.orderId}
    </p>
  </div>
);

const BarcodeSection = ({ label, value, height = "50px" }) => (
  <div className="w-[45%] flex flex-col items-center justify-center">
    <img
      src={generateBarcodeURL(value)}
      alt={`${label} Barcode`}
      className={`w-full px-2.5 h-[${height}]`}
    />
    <p>
      <b>{label}:</b> {value}
    </p>
  </div>
);

const ProductTable = ({ products }) => (
  <table className="w-full border-collapse border border-black">
    <thead>
      <tr>
        <th className="border border-black">SKU</th>
        <th colSpan={2} className="border border-black">
          Product
        </th>
        <th className="border border-black">Qty</th>
        <th className="border border-black">Amount</th>
      </tr>
    </thead>
    <tbody>
      {products.map((product) => (
        <tr key={product.sku}>
          <td className="border border-black px-1">{product.sku}</td>
          <td colSpan={2} className="border border-black px-1">
            {product.name}
          </td>
          <td className="border border-black px-1">{product.qty}</td>
          <td className="border border-black font-nunito px-1">
            ₹{product.amount}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

function generateBarcodeURL(data) {
  // Using a more reliable barcode generation service
  return `https://barcodeapi.org/api/code128/${encodeURIComponent(data)}`;
}

const ShippingLabel = ({
  config = {
    includeLogo: true,
    showPickupReturnAddress: true,
    showProductDetails: true,
    showProductPrice: true,
    hideMobileNumber: false,
  },
  recipientDetails = {
    name: "John Doe",
    address: "123 Street Name",
    city: "Mumbai",
    state: "Maharashtra",
    zip: "400001",
    landmark: "Near Central Park",
    mobile: ["9876543210", "9123456789"],
  },
  orderDetails = {
    codAmount: "500",
    invoiceNo: "INV123456",
    shippingId: "SHIP7890",
    orderId: "ORD456789",
  },
  products = [
    { sku: "SKU123321", name: "Product A", qty: 2, amount: 300 },
    { sku: "SKU123543", name: "Product B", qty: 1, amount: 200 },
    { sku: "SKU123678", name: "Product C", qty: 3, amount: 450 },
    { sku: "SKU123999", name: "Product D", qty: 1, amount: 150 },
    { sku: "SKU124000", name: "Product E", qty: 5, amount: 750 },
  ],
  courierInfo = {
    partner: "Xpress Bees",
    awbNo: "AWB123456789",
  },
  pickupDetails = {
    address: "W4rehouse 1, Mumbai, Maharashtra - 400001",
    mobile: "9876543210",
    gst: "27AABCU9603R1ZM",
  },
  logoUrl = "https://localhost500/uploads/logo.png",
  copies = 1,
}) => {
  const LabelContainer = ({ children }) => (
    <div className="p-0 border border-black w-[104mm] h-[147.5mm] flex flex-col text-xs font-sans box-border bg-white">
      {children}
    </div>
  );

  return (
    <div className="flex w-full h-fit box-border justify-center overflow-hidden mx-auto">
      {[...Array(copies)].map((_, index) => (
        <LabelContainer key={index}>
          <div className="p-1 flex justify-between items-center leading-tight border-b border-black">
            <AddressSection
              recipientDetails={recipientDetails}
              hideMobileNumber={config.hideMobileNumber}
            />
            <LogoSection includeLogo={config.includeLogo} logoUrl={logoUrl} />
          </div>

          <div className="px-5 py-2 flex justify-between border-y border-black">
            <p className="text-lg font-bold">COD</p>
            {config.showProductPrice && (
              <p className="text-lg font-bold font-nunito">
                ₹{orderDetails.codAmount}
              </p>
            )}
          </div>

          <div className="flex justify-between flex-1 items-center px-4 py-2 border-b border-black">
            <OrderInfoSection orderDetails={orderDetails} />
            <BarcodeSection label="Order ID" value={orderDetails.orderId} />
          </div>

          {config.showProductDetails && <ProductTable products={products} />}

          <div className="flex justify-between flex-1 items-center overflow-hidden px-4 p-1 border-t border-black">
            <div className="text-center w-[40%] flex flex-col items-center justify-center">
              <p className="mb-1">
                <b>Courier Partner:</b>
              </p>
              <p className="text-sm font-medium">{courierInfo.partner}</p>
            </div>
            <BarcodeSection label="AWB No" value={courierInfo.awbNo} />
          </div>

          {config.showPickupReturnAddress && (
            <div className="p-1 flex-1 border-t border-black">
              <p>
                <b>Pickup & Return Address:</b>
              </p>
              <p>{pickupDetails.address}</p>
              <p>Mobile: {pickupDetails.mobile}</p>
              <p>GST: {pickupDetails.gst}</p>
            </div>
          )}
        </LabelContainer>
      ))}
    </div>
  );
};

export default ShippingLabel;
